import { useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, IconButton, InputAdornment, TextField } from '@mui/material'

import { UserContext } from '../../contexts'
import { numberWithCommas } from '../../utils'
import { FormError, Payment } from '../../types'
import TrashIcon from '../../assets/icons/trash-icon.svg'
import FilterIcon from '../../assets/icons/filter-icon.svg'
import SearchIcon from '../../assets/icons/search-icon.svg'
import { baseButtonWarning, textFieldWhiteBg } from '../../theme/custom-styles'

export const ActionsBar = ({
  errors,
  className,
  paymentsData,
  paymentToSearch,
  showDeleteButton,
  paymentsPocketData,
  processingDocument,
  searchingInDatabase,
  showPocketsPayments,
  generalActionChecked,
  handleClearSearchInput,
  handleSetPaymentToSearch,
  handleOpenDeletePaymentModal,
  handleOpenFiltersOptionsModal,
  handleToggleGeneralActionChecked
}: {
  errors: FormError
  className?: string
  paymentToSearch: string
  showDeleteButton?: boolean
  processingDocument?: boolean
  searchingInDatabase?: boolean
  showPocketsPayments?: boolean
  generalActionChecked?: boolean
  handleClearSearchInput: () => void
  handleOpenDeletePaymentModal?: () => void
  handleOpenFiltersOptionsModal?: () => void
  paymentsData: Array<Payment | Partial<Payment>>
  paymentsPocketData?: Array<Payment | Partial<Payment>>
  handleToggleGeneralActionChecked?: (checked: boolean) => void
  handleSetPaymentToSearch: (paymentToSearchData: string, showError: boolean) => void
}) => {
  const location = useLocation()
  const { userSession } = useContext(UserContext)
  const { t } = useTranslation(['payments-to-review-management'])

  return (
    <div
      style={{ marginTop: '28px' }}
      className={className ? `actions-bar-container ${className}` : 'actions-bar-container'}
    >
      <div className="actions-bar-left">
        <div className="users-management-actions-bar-check-option">
          {!processingDocument && userSession && userSession.role.indexOf('-client') === -1 && (
            <Checkbox
              checked={paymentsData.length === 0 ? false : generalActionChecked === true ? true : false}
              disabled={paymentsData.length === 0}
              onChange={(event) => handleToggleGeneralActionChecked?.(event.target.checked)}
            />
          )}
          {!showPocketsPayments && (
            <>
              {paymentsData.length === 0 ? (
                <div className="users-management-actions-bar-check-option-text">
                  {className === 'payments-completed-app-bar'
                    ? t('actionsBar.emptyListCompletedMessage', { paymentWord: t('wordPaymentPlural').toLowerCase() })
                    : t('actionsBar.emptyListMessage', { paymentWord: t('wordPaymentPlural').toLowerCase() })}
                </div>
              ) : (
                <div className="users-management-actions-bar-check-option-text">
                  {location.pathname.indexOf('payments-rejected') > -1
                    ? t('actionsBar.rejectedPaymentsCountPlural')
                    : location.pathname.indexOf('payments-returned') > -1
                    ? t('actionsBar.returnedPaymentsCountPlural')
                    : location.pathname.indexOf('payments-completed') > -1
                    ? t('actionsBar.paidPaymentsCountPlural')
                    : location.pathname.indexOf('payments-validated') > -1
                    ? t('actionsBar.validatedPaymentsCountPlural')
                    : className === 'payments-completed-app-bar'
                    ? t('actionsBar.paymentsCompletedCount', { paymentsCount: numberWithCommas(paymentsData.length), paymentWord: paymentsData.length === 1 ? t('wordPaymentSingular') : `${t('wordPaymentPlural')}` })
                    : location.state?.previousPath && location.state?.previousPath === `customer-company-payments/${userSession?.belongsTo}`
                    ? t('actionsBar.pendingPaymentsPlural')
                    : t('actionsBar.paymentsCountPlural')}
                </div>
              )}
            </>
          )}
          {showPocketsPayments && Array.isArray(paymentsPocketData) && (
            <>
              {paymentsPocketData.length === 0 ? (
                <div className="users-management-actions-bar-check-option-text">
                  {className === 'payments-completed-app-bar'
                    ? t('actionsBar.emptyListCompletedMessage', { paymentWord: t('wordPaymentPlural').toLowerCase() })
                    : t('actionsBar.emptyListMessage', { paymentWord: t('wordPaymentPlural').toLowerCase() })}
                </div>
              ) : (
                <div className="users-management-actions-bar-check-option-text">
                  {className === 'payments-completed-app-bar'
                    ? t('actionsBar.paymentsCompletedCount', {
                        paymentsCount: numberWithCommas(paymentsPocketData.length),
                        paymentWord: paymentsPocketData.length === 1 ? t('wordPaymentSingular') : `${t('wordPaymentPlural')}`
                      })
                    : t('actionsBar.paymentsCount', { paymentsCount: numberWithCommas(paymentsPocketData.length), paymentWord: paymentsPocketData.length === 1 ? t('wordPaymentSingular') : `${t('wordPaymentPlural')}` })}
                </div>
              )}
            </>
          )}
        </div>
        <div className="users-management-actions-bar-button-action-container">
          {showDeleteButton && !processingDocument && (
            <Button
              fullWidth
              variant="text"
              sx={baseButtonWarning}
              startIcon={
                <img
                  src={TrashIcon}
                  alt="trash-icon"
                />
              }
              className="users-management-actions-bar-button-action"
              onClick={() => handleOpenDeletePaymentModal?.()}
            >
              <div className="users-management-actions-bar-button-action-text">{t('paymentsActionBar.buttonsTexts.deletePayment')}</div>
            </Button>
          )}
          {userSession && (!userSession.subRole || (userSession.subRole && userSession.subRole !== 'compliance-specialist')) && (
            <div
              style={{ cursor: 'pointer' }}
              className="filter-button-container"
              onClick={() => handleOpenFiltersOptionsModal?.()}
            >
              {/* !fromDateToSearch && !toDateToSearch && !beneficiaryToSearch ? (
                    <> */}
              <img
                src={FilterIcon}
                alt="filter-icon"
              />
              <div className="filter-button-text">{t('actionsBar.filter', { ns: 'customer-details-management' })}</div>
              {/* </>
                  ) : (
                    <>
                      <div className="filter-button-text">
                        {beneficiaryToSearch && !fromDateToSearch && !toDateToSearch
                          ? beneficiaryToSearch.split('-')[1]
                          : (fromDateToSearch || toDateToSearch) && !beneficiaryToSearch
                          ? `${format(addHours(new Date(fromDateToSearch as Date), 5), 'dd/MM/yyyy')} - ${format(addHours(new Date(toDateToSearch as Date), 5), 'dd/MM/yyyy')}`
                          : ''}
                      </div>
                      <div
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        onClick={() => {
                          setToDateToSearch(null)
                          setFromDateToSearch(null)
                          setBeneficiaryToSearch('')
                          setOpenPaymentFiltersModal(false)
                          setCustomerCompanyPaymentToSearch('')
                          handleSetCustomerCompanyPaymentToSearch('', false)
                        }}
                      >
                        <img
                          src={CancelIcon}
                          alt="cancel-icon"
                        />
                      </div>
                    </>
                  ) */}
            </div>
          )}
        </div>
      </div>
      <div className="actions-bar-right">
        <TextField
          fullWidth
          variant="standard"
          sx={textFieldWhiteBg}
          value={paymentToSearch}
          disabled={searchingInDatabase}
          error={!!errors.paymentToSearch}
          helperText={errors.paymentToSearch}
          className="actions-bar-search-input"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              handleSetPaymentToSearch(paymentToSearch, true)
            }
          }}
          onChange={(event) => {
            if (event.target.value === '') {
              handleSetPaymentToSearch(event.target.value, false)
              return
            }

            handleSetPaymentToSearch(event.target.value, true)
          }}
          onBlur={() => {
            if (paymentToSearch === '') handleClearSearchInput()
          }}
          label={t('actionsBar.searchFieldText', { paymentWord: t('wordPaymentSingular') })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  aria-label="toggle password visibility"
                  sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                >
                  <img
                    src={SearchIcon}
                    alt="search-icon"
                  />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </div>
    </div>
  )
}
